/**
 * @file src/auth/firebaseClient.js
 * @description Lazy-loads Firebase app/auth modules from the CDN and exposes helpers to
 * reuse a single instance across the Orbiters runtime.
 *
 * ## The SDK is off-origin, and an offline boot must not reach for it
 *
 * Both modules below are imported from `www.gstatic.com` at runtime — they are not in the build, so
 * precaching the build does not make them available. That is left as it is on purpose: an offline
 * session cannot sign anyone in whether or not the SDK loads, because signing in is a conversation
 * with a server. Caching megabytes of an SDK to reach a screen that cannot work is worse than not
 * having it.
 *
 * What the guard buys is the difference between degrading and hanging. An unguarded dynamic import
 * with no network does not fail quickly — it fails after the browser's own connection timeout,
 * having first shown a red network error, and every caller awaiting it waits that long. Refusing up
 * front turns that into an immediate `null`, which is the same answer every caller here already
 * handles for a missing config.
 */
import { isOfflineReadOnly } from '../offline/offlineMode.js';

const FIREBASE_VERSION = '11.9.1';

let firebaseAppInstance = null;
let appInitPromise = null;
let authModulePromise = null;

/**
 * Reads config values from Vite env or window globals (whichever is available).
 * @param {string} key
 * @returns {string|null}
 */
function readEnv(key) {
  const fromImportMeta = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env[key] : undefined;
  const fromWindow = typeof window !== 'undefined' ? window[key] : undefined;
  return fromImportMeta || fromWindow || null;
}

/**
 * Builds the minimal Firebase config needed for initialiseApp. Returns null if
 * required keys are missing so callers can short-circuit.
 * @returns {{ apiKey: string, authDomain: string, projectId: string }|null}
 */
function getFirebaseConfig() {
  const apiKey = readEnv('VITE_PUBLIC_FIREBASE_API_KEY');
  const authDomain = readEnv('VITE_PUBLIC_FIREBASE_AUTH_DOMAIN');
  const projectId = readEnv('VITE_PUBLIC_FIREBASE_PROJECT_ID');

  if (!apiKey || !authDomain || !projectId) {
    console.warn('[Auth] Firebase config incomplete; skipping client init.', {
      hasApiKey: Boolean(apiKey),
      hasAuthDomain: Boolean(authDomain),
      hasProjectId: Boolean(projectId),
    });
    return null;
  }

  return {
    apiKey,
    authDomain,
    projectId,
  };
}

/**
 * Resolves (and caches) the singleton Firebase app instance, loading the SDK dynamically.
 * @returns {Promise<import('firebase/app').FirebaseApp|null>}
 */
export async function getFirebaseApp() {
  if (firebaseAppInstance) return firebaseAppInstance;
  if (appInitPromise) return appInitPromise;

  // An already-loaded SDK is still usable; a fresh fetch of one is not. See the file header.
  if (isOfflineReadOnly()) return null;

  const config = getFirebaseConfig();
  if (!config) {
    return null;
  }

  appInitPromise = (async () => {
    const { initializeApp, getApps } = await import(
      /* @vite-ignore */ /* webpackIgnore: true */ `https://www.gstatic.com/firebasejs/${FIREBASE_VERSION}/firebase-app.js`
    );
    const apps = getApps();
    firebaseAppInstance = apps && apps.length ? apps[0] : initializeApp(config);
    return firebaseAppInstance;
  })();

  try {
    return await appInitPromise;
  } finally {
    appInitPromise = null;
  }
}

/**
 * Returns the already-initialized Firebase app, or null — never triggers SDK loading.
 * Lets callers act on an existing auth session (e.g. sign-out) without booting Firebase.
 * @returns {import('firebase/app').FirebaseApp|null}
 */
export function getLoadedFirebaseApp() {
  return firebaseAppInstance;
}

/**
 * Loads the Firebase auth module from the CDN and caches the promise.
 * @returns {Promise<typeof import('firebase/auth')>}
 */
export async function getFirebaseAuthModule() {
  if (authModulePromise) {
    return authModulePromise;
  }

  // Null rather than a hung import. Callers already treat a missing module as "no Firebase auth
  // here", which is the truth of an offline session.
  if (isOfflineReadOnly()) return null;

  authModulePromise = import(
    /* @vite-ignore */ /* webpackIgnore: true */ `https://www.gstatic.com/firebasejs/${FIREBASE_VERSION}/firebase-auth.js`
  );
  try {
    return await authModulePromise;
  } finally {
    authModulePromise = null;
  }
}
